import { cn } from "@/lib/utils";
import { CloudRain } from "lucide-react";

const THREATS = {
  hail: { label: "Hail" },
  wind: { label: "Wind" },
  flooding: { label: "Flooding" },
  mcs: { label: "MCS Risk" },
  mesocyclonic: { label: "Tornado" },
  supercells: { label: "Supercells" }
};

// Get color based on risk percentage
const getRiskColor = (risk) => {
  if (risk >= 60) return { color: "#EF4444", name: "red" }; // Red
  if (risk >= 40) return { color: "#F97316", name: "orange" }; // Orange
  if (risk >= 20) return { color: "#FBBF24", name: "yellow" }; // Yellow
  return { color: "#22C55E", name: "green" }; // Green
};

export function ThreatIndicator({ type, active, risk = 0, className }) {
  const threat = THREATS[type];
  if (!threat) return null;
  
  const riskColor = getRiskColor(risk);
  const showRisk = active && risk > 0;
  
  return (
    <div
      className={cn(
        "relative flex items-center justify-between px-3 py-2 border transition-all overflow-hidden",
        active 
          ? "bg-[#1a1a1a] border-[#FBBF24]/30 text-[#F9FAFB]" 
          : "bg-[#1a1a1a] border-white/5 text-[#6B7280]",
        className
      )}
      data-testid={`threat-${type}`}
    >
      {/* Fill bar: left-to-right proportional to risk %, right end more opaque */}
      {showRisk && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            width: `${risk}%`,
            background: `linear-gradient(to right, ${riskColor.color}18, ${riskColor.color}45)`,
          }}
        />
      )}
      
      <span className="relative text-xs font-medium uppercase tracking-wider z-10">
        {threat.label}
      </span>
      
      {showRisk && (
        <span 
          className="relative text-xs font-bold font-mono z-10"
          style={{ color: riskColor.color }}
        >
          {risk}%
        </span>
      )}
      
      {active && !showRisk && (
        <span className="relative w-2 h-2 rounded-full bg-[#FBBF24] z-10" />
      )}
    </div>
  );
}

export function ThreatBreakdown({ outlook, className }) {
  const threatTypes = ["hail", "wind", "flooding", "mcs", "mesocyclonic", "supercells"];
  const activeThreats = threatTypes.filter(t => outlook[t]);
  
  return (
    <div className={cn("space-y-3", className)} data-testid="threat-breakdown">
      <h4 className="font-heading text-xs uppercase tracking-widest text-[#6B7280] flex items-center gap-2">
        <CloudRain className="w-4 h-4" />
        Threat Breakdown
      </h4>
      
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {threatTypes.map((type) => (
          <ThreatIndicator
            key={type}
            type={type}
            active={outlook[type]}
            risk={outlook[`${type}_risk`] || 0}
          />
        ))}
      </div>
      
      {activeThreats.length === 0 && (
        <p className="text-xs text-[#6B7280] italic">
          No significant threats identified
        </p>
      )}
    </div>
  );
}

// Compact version for archive cards
export function ThreatBadges({ outlook, className }) {
  const threatTypes = ["hail", "wind", "flooding", "mcs", "mesocyclonic", "supercells"];
  const activeThreats = threatTypes.filter(t => outlook[t]);
  
  if (activeThreats.length === 0) return null;
  
  return (
    <div className={cn("flex flex-wrap gap-1", className)}>
      {activeThreats.map((type) => {
        const risk = outlook[`${type}_risk`] || 0;
        const riskColor = getRiskColor(risk);
        return (
          <span
            key={type}
            className="px-2 py-0.5 text-[9px] uppercase tracking-wider font-medium border"
            style={{
              color: riskColor.color,
              borderColor: `${riskColor.color}50`,
              backgroundColor: `${riskColor.color}10`
            }}
          >
            {THREATS[type]?.label}
            {risk > 0 && ` ${risk}%`}
          </span>
        );
      })}
    </div>
  );
}

export { THREATS };
